import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ACTION_LABELS, MODULE_LABELS } from "../../config/permissionMenuMap";
import {
  cloneStaffPermissions,
  createPermissionTemplate,
  fetchPermissionsGrouped,
  fetchRoleTemplates,
  fetchStaffListForClone,
} from "../../services/rbacApi";
import "./StaffPermissionsEditor.css";

export default function StaffPermissionsEditor({
  staffId,
  roleId: initialRoleId,
  permissionIds: initialPermissionIds = [],
  onChange,
  disabled = false,
}) {
  const [grouped, setGrouped] = useState({});
  const [templates, setTemplates] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(initialRoleId || "");
  const [selectedIds, setSelectedIds] = useState(new Set(initialPermissionIds.map(Number)));
  const [cloneSourceId, setCloneSourceId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const [groupedData, templateData, staffData] = await Promise.all([
          fetchPermissionsGrouped(),
          fetchRoleTemplates(),
          fetchStaffListForClone(),
        ]);
        if (!active) return;
        setGrouped(groupedData || {});
        setTemplates(templateData || []);
        setStaffList(Array.isArray(staffData) ? staffData : []);
      } catch (err) {
        toast.error(err.message || "Could not load permissions");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setSelectedRoleId(initialRoleId || "");
  }, [initialRoleId]);

  useEffect(() => {
    setSelectedIds(new Set((initialPermissionIds || []).map(Number)));
  }, [initialPermissionIds]);

  const notifyChange = (nextRoleId, nextIds) => {
    onChange?.({
      roleId: nextRoleId || null,
      permissionIds: [...nextIds],
    });
  };

  const togglePermission = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      notifyChange(selectedRoleId, next);
      return next;
    });
  };

  const toggleModule = (moduleKey, checked) => {
    const modulePerms = grouped[moduleKey] || [];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const p of modulePerms) {
        if (checked) next.add(p.id);
        else next.delete(p.id);
      }
      notifyChange(selectedRoleId, next);
      return next;
    });
  };

  const applyTemplate = (template) => {
    if (!template) return;
    const ids = (template.permissions || []).map((p) => p.id);
    setSelectedRoleId(template.id);
    setSelectedIds(new Set(ids));
    notifyChange(template.id, new Set(ids));
  };

  const handleClone = async () => {
    if (!cloneSourceId || !staffId) {
      toast.error("Select a staff member to clone from");
      return;
    }
    try {
      const payload = await cloneStaffPermissions(staffId, cloneSourceId);
      const ids = await resolveIdsFromSlugs(payload.permissions);
      setSelectedRoleId(payload.roleId || "");
      setSelectedIds(new Set(ids));
      notifyChange(payload.roleId, new Set(ids));
      toast.success("Permissions cloned");
    } catch (err) {
      toast.error(err.message || "Clone failed");
    }
  };

  async function resolveIdsFromSlugs(slugs = []) {
    const all = Object.values(grouped).flat();
    return all.filter((p) => slugs.includes(p.slug)).map((p) => p.id);
  }

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Enter a template name");
      return;
    }
    setSavingTemplate(true);
    try {
      const slug = templateName.trim().toLowerCase().replace(/\s+/g, "-");
      await createPermissionTemplate({
        name: templateName.trim(),
        slug,
        permissionIds: [...selectedIds],
      });
      toast.success("Permission template saved");
      setTemplateName("");
      const templateData = await fetchRoleTemplates();
      setTemplates(templateData || []);
    } catch (err) {
      toast.error(err.message || "Could not save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const moduleKeys = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  if (loading) {
    return (
      <div className="product-form-page__section">
        <p className="product-form-page__section-desc">Loading permissions…</p>
      </div>
    );
  }

  return (
    <div className="product-form-page__section staff-perm-editor">
      <h2 className="product-form-page__section-title">Permissions</h2>
      <p className="product-form-page__section-desc">
        Grant feature-level access. Staff only sees menus and API actions allowed here.
      </p>

      <div className="staff-perm-editor__toolbar">
        <div className="staff-perm-editor__field">
          <span className="staff-perm-editor__field-label">Apply template</span>
          <select
            value={selectedRoleId}
            onChange={(e) => {
              const id = e.target.value;
              const template = templates.find((t) => String(t.id) === String(id));
              if (template) applyTemplate(template);
              else {
                setSelectedRoleId("");
                notifyChange(null, selectedIds);
              }
            }}
            disabled={disabled}
          >
            <option value="">Custom selection</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {staffId ? (
          <div className="staff-perm-editor__field">
            <span className="staff-perm-editor__field-label">Clone from staff</span>
            <div className="staff-perm-editor__inline">
              <select
                value={cloneSourceId}
                onChange={(e) => setCloneSourceId(e.target.value)}
                disabled={disabled}
              >
                <option value="">Select staff</option>
                {staffList
                  .filter((s) => String(s.id) !== String(staffId))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.firstName} {s.lastName} ({s.email})
                    </option>
                  ))}
              </select>
              <button type="button" className="staff-perm-editor__btn" onClick={handleClone} disabled={disabled}>
                Clone
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="staff-perm-editor__grid">
        {moduleKeys.map((moduleKey) => {
          const perms = grouped[moduleKey] || [];
          const allChecked = perms.length > 0 && perms.every((p) => selectedIds.has(p.id));
          const someChecked = perms.some((p) => selectedIds.has(p.id));
          const moduleInputId = `perm-module-${moduleKey}`;

          return (
            <div key={moduleKey} className="staff-perm-editor__card">
              <div className="staff-perm-editor__card-head">
                <input
                  id={moduleInputId}
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = !allChecked && someChecked;
                  }}
                  onChange={(e) => toggleModule(moduleKey, e.target.checked)}
                  disabled={disabled}
                />
                <label className="staff-perm-editor__module-name" htmlFor={moduleInputId}>
                  {MODULE_LABELS[moduleKey] || moduleKey}
                </label>
              </div>

              <div className="staff-perm-editor__actions">
                {perms.map((perm) => {
                  const inputId = `perm-${perm.id}`;
                  return (
                    <div
                      key={perm.id}
                      className="staff-perm-editor__action"
                      onClick={() => !disabled && togglePermission(perm.id)}
                      onKeyDown={(e) => {
                        if (disabled) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          togglePermission(perm.id);
                        }
                      }}
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={selectedIds.has(perm.id)}
                        onChange={() => togglePermission(perm.id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={disabled}
                      />
                      <span className="staff-perm-editor__action-text">
                        {ACTION_LABELS[perm.action] || perm.action}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="staff-perm-editor__footer">
        <div className="staff-perm-editor__field">
          <span className="staff-perm-editor__field-label">Save as custom template</span>
          <div className="staff-perm-editor__inline">
            <input
              type="text"
              placeholder="Template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              disabled={disabled || savingTemplate}
            />
            <button
              type="button"
              className="staff-perm-editor__btn"
              onClick={handleSaveTemplate}
              disabled={disabled || savingTemplate}
            >
              {savingTemplate ? "Saving…" : "Save template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
